// src/pages/EmailVerification.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Alert from '../components/ui/Alert';
import Button from '../components/ui/Button';
import { auth } from '../services/api';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const EmailVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setError('Invalid verification link. Please check your email or request a new verification link.');
        return;
      }

      try {
        await auth.verifyEmail(token);
        setStatus('success');

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err) {
        setStatus('error');
        setError(err.response?.data?.error || 'Verification failed. The link may have expired.');
      }
    };

    verifyEmail();
  }, [token, navigate]);

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black pt-24 md:pt-28 lg:pt-32 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="max-w-md w-full mx-4 relative z-16">
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-8 shadow-2xl text-center">
            <div className="flex justify-center mb-6">
              <Loader2 className="w-16 h-16 text-purple-400 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Verifying Your Email</h2>
            <p className="text-gray-400">
              Please wait while we verify your email address...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black pt-24 md:pt-28 lg:pt-32 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="max-w-md w-full mx-4 relative z-16">
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-8 shadow-2xl text-center">
            <div className="flex justify-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Email Verified Successfully!</h2>
            <p className="text-gray-400 mb-6">
              Your email has been verified. You can now sign in to your account.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              You will be redirected to the login page automatically...
            </p>
            <Link
              to="/login"
              className="inline-block"
            >
              <Button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-lg hover:shadow-purple-500/50">
                Go to Login Now
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black pt-24 md:pt-28 lg:pt-32 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="max-w-md w-full mx-4 relative z-16">
          <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-xl p-8 shadow-2xl text-center">
            <div className="flex justify-center mb-6">
              <XCircle className="w-16 h-16 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Verification Failed</h2>

            <Alert variant="destructive" className="mb-6 bg-red-500/10 border-red-500/50 text-red-400">
              {error}
            </Alert>

            <div className="space-y-4">
              <p className="text-gray-400 text-sm">
                The verification link may have expired or is invalid. You can request a new verification email from your profile page after logging in.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/login">
                  <Button className="w-full sm:w-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-lg hover:shadow-purple-500/50">
                    Go to Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="w-full sm:w-auto bg-slate-700 hover:bg-slate-600">
                    Create New Account
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-500 text-sm mt-6">
            Need help?{' '}
            <a href="mailto:contact@strayapps.org" className="text-purple-400 hover:text-purple-300">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default EmailVerification;
